// Aktarım menüsü — dışa/içe aktar dropdown + import önizleme modal.
// FormManager header'ına mount edilir; ayrı bileşen tutulur (FormManager çok büyük).
//
// Kit design review (2026-09-08, spec §13) sözleşmesi:
//  D3  durumsal başlık + sakin mikrocopy (hata varsa "henüz içe aktarılamıyor — N hata")
//  D4  durum matrisi: yalnız hata / yalnız uyarı / ikisi / çok madde (ilk 8 + "+N daha", scroll),
//      "Hata ve uyarıları kopyala" (cap'siz), slice(0,10) sessiz kesme YOK, max-w-2xl
//  D5  geçersiz JSON → teşhisli mesaj (diagnoseJsonParseFailure, shared)
//  D10 amber uyarı bloğu yeni token'larla (--color-warning-text/-bg/-border); --color-warning KULLANILMAZ
//  D12 a11y: role=dialog, aria-labelledby, açılışta odak başlığa, Esc kapatır, odak tuzağı,
//      hata bölgesi role=alert, uyarı bölgesi aria-live=polite, kopyala aria-label + duyuru
import { useEffect, useId, useRef, useState } from "react";
import {
  ALL_PERIODS,
  diagnoseJsonParseFailure,
  stripSectorPrefix,
  type PeriodType,
} from "@mahir/shared";
import { ApiError } from "../../api/client";
import {
  exportFrameworkVersion,
  importFrameworkDryRun,
  importFrameworkCommit,
  importVersionDryRun,
  importVersionCommit,
  type ImportResult,
  type ImportTargetSummary,
} from "../../api/frameworks";
import { PERIOD_SHORT_LABELS } from "../../lib/period-ui";

/** İçe aktarma hedefi: yeni framework mü, seçili framework'e yeni sürüm mü (FW-04). */
export type ImportMode = "framework" | "version";

export interface ImportedInfo {
  sectionCount: number;
  questionCount: number;
  /** Bilinmeyen alan uyarıları: içerik atıldı (bilgi kaybı). */
  warningsIgnored: number;
  /** "kalite: …" uyarıları: hiçbir şey atılmadı, yalnız kalite notu (bloklamaz). */
  qualityWarnings: number;
  /** FW-04 — hangi moda göre içe aktarıldı (başarı bandı metnini belirler). */
  mode: ImportMode;
  /** FW-04 — yalnız `mode === "version"`: yeni açılan taslak sürümün id'si. */
  versionId?: string;
  /** FW-04 — yalnız `mode === "version"`: dosyadan gelen sürüm numarası (örn. "v3"). */
  versionNumber?: string;
}

/** Kalite uyarıları backend'de bu önekle gelir (services/framework-quality.ts). */
const QUALITY_PREFIX = "kalite: ";

/**
 * Commit sonrası başarı bandı için uyarıları iki anlama ayırır: kalite uyarısı hiçbir içeriğin
 * atılmadığı bilgilendirme, "yok sayıldı" ise bilinmeyen alan (içerik kaybı).
 */
export function splitImportWarnings(warnings: readonly string[]): {
  quality: number;
  ignored: number;
} {
  const quality = warnings.filter((w) => w.startsWith(QUALITY_PREFIX)).length;
  return { quality, ignored: warnings.length - quality };
}

/**
 * FW-04 — hedefli import önizlemesindeki tek satırlık fark özeti.
 * Sıfır olan kalemler atlanır; framework'ün hiç sürümü yoksa taban da yoktur → "ilk sürüm".
 */
export function diffSummaryLabel(t: ImportTargetSummary): string {
  // Çeyreklik anahtarı eki TABANDAN BAĞIMSIZ hesaplanır: commit, dosyada anahtar varsa alanı
  // hiç sürümü olmayan framework'te de yazar — "ilk sürüm" dalı eki düşürürse önizleme ile
  // commit ayrışır (yetki taşıyan alan sessizce kapanabilirdi).
  const quarterlySuffix = t.supportsQuarterlyChanged
    ? ` · çeyreklik: ${t.supportsQuarterlyChanged.before ? "açık→kapalı" : "kapalı→açık"}`
    : "";
  if (t.baseVersionNumber === null) return `ilk sürüm${quarterlySuffix}`;
  const parts: string[] = [];
  if (t.added.length > 0) parts.push(`+${t.added.length.toString()} soru`);
  if (t.removed.length > 0) parts.push(`−${t.removed.length.toString()} soru`);
  if (t.typeChanged.length > 0) parts.push(`${t.typeChanged.length.toString()} tip değişikliği`);
  const body = parts.length > 0 ? parts.join(" · ") : "soru listesi değişmiyor";
  let label = `${t.baseVersionNumber} tabanına göre: ${body}`;
  // K7 D3 — dönem alanları optional (F1): backend her zaman doldurur, eski/test literal'ları
  // alansız da derlensin diye burada yalnız DOLUYSA ek satır üretilir.
  if (t.periodChanged && t.periodChanged.length > 0) {
    const sectionCount = t.periodChanged.filter((p) => !p.key.includes("/")).length;
    const questionCount = t.periodChanged.length - sectionCount;
    label += ` · dönem: ${sectionCount.toString()} bölüm, ${questionCount.toString()} soru`;
  }
  return `${label}${quarterlySuffix}`;
}

/** K7 D3 — kısa dönem etiketi (tek kopya: period-ui); dördü de seçiliyse "Tüm dönemler". */
export function periodListLabel(periods: readonly PeriodType[]): string {
  if (periods.length === ALL_PERIODS.length) return "Tüm dönemler";
  return periods.map((p) => PERIOD_SHORT_LABELS[p]).join(", ");
}

/** K7 D3 — hedefli önizlemede "Dönem değişikliklerini göster" altında satır başına özet. */
export function periodChangeLine(c: {
  key: string;
  before: PeriodType[];
  after: PeriodType[];
}): string {
  return `${c.key}: ${periodListLabel(c.before)} → ${periodListLabel(c.after)}`;
}

/** K7 D3 — dryRun warnings'inde "etkin dönem yok" satır sayısı (amber blokta ek not). */
export function countZeroEffectivePeriodWarnings(warnings: readonly string[]): number {
  return warnings.filter((w) => w.includes("etkin dönem yok, soru hiç sorulmaz")).length;
}

/**
 * FW-04 — hedefli önizlemede şema hatası dosyayı hiç ayrıştıramadığından `target`'ın fark
 * listeleri (added/removed/typeChanged) boş gelir; bu durumda "soru listesi değişmiyor" YALAN
 * olur. `sectionCount`/`questionCount` sıfırsa dosya parse edilmemiş demektir → fark yerine
 * "okunamadı" mesajı gösterilir.
 */
export function targetLine(summary: ImportResult["summary"]): string {
  const t = summary.target;
  if (!t) return "";
  const parsedOk = summary.sectionCount > 0 || summary.questionCount > 0;
  return `${t.frameworkName}${
    parsedOk ? ` · ${diffSummaryLabel(t)}` : " · dosya okunamadı, fark hesaplanamadı"
  }`;
}

/**
 * Hedefli import'un iki bloklayıcı 409'u için eyleme dönük ipucu. Diğer hatalarda ipucu yok
 * (mesaj zaten backend'den açıklayıcı gelir).
 */
function hintsForApiError(err: unknown): string[] {
  if (!(err instanceof ApiError)) return [];
  if (err.code === "draft_exists") return ["Önce mevcut taslağı yayınlayın ya da silin"];
  if (err.code === "version_conflict") return ["Dosyadaki versionNumber'ı artırın (örn. v2 → v3)"];
  return [];
}

interface Props {
  frameworkId: string | null;
  versionId: string | null;
  onImported: (newFrameworkId: string, info: ImportedInfo) => void;
}

const LIST_PREVIEW_COUNT = 8;

interface ErrorBox {
  title: string;
  hints: string[];
}

export function TransferMenu({ frameworkId, versionId, onImported }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{
    result: ImportResult;
    payload: unknown;
    /** Hedef, önizleme anında dondurulur: onay dosyanın doğrulandığı framework'e gider. */
    target: { frameworkId: string } | null;
  } | null>(null);
  const [error, setError] = useState<ErrorBox | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Dosya seçici tek input — hangi menü öğesinden açıldığını ref tutar (onChange anında okunur;
  // state kullanılsaydı aynı tick'teki .click() bayat değeri görebilirdi).
  const modeRef = useRef<ImportMode>("framework");

  async function handleExport(): Promise<void> {
    if (!frameworkId || !versionId) return;
    setOpen(false);
    setBusy(true);
    try {
      await exportFrameworkVersion(frameworkId, versionId);
    } catch {
      setError({ title: "Dışa aktarma başarısız", hints: [] });
    } finally {
      setBusy(false);
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    setOpen(false);
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (modeRef.current === "version" && !frameworkId) {
      // Buton disabled olduğunda buraya normalde girilmez — savunma amaçlı: frameworkId
      // yoksa sessizce "yeni framework" moduna düşmek yerine açıkça durdur (yanlış mod feci olur).
      setError({ title: "Önce bir çerçeve seçin", hints: [] });
      return;
    }
    const target = modeRef.current === "version" && frameworkId ? { frameworkId } : null;
    setBusy(true);
    try {
      const text = await file.text();
      let payload: unknown;
      try {
        payload = JSON.parse(text);
      } catch {
        setError(diagnoseJsonParseFailure(text));
        return;
      }
      const result = target
        ? await importVersionDryRun(target.frameworkId, payload)
        : await importFrameworkDryRun(payload);
      setPreview({ result, payload, target });
    } catch (err) {
      setError({
        title: err instanceof ApiError ? err.message : "Dosya okunamadı veya doğrulama başarısız",
        hints: hintsForApiError(err),
      });
    } finally {
      setBusy(false);
    }
  }

  async function confirmImport(): Promise<void> {
    if (!preview || preview.result.errors.length > 0) return;
    const { target } = preview;
    const mode: ImportMode = target ? "version" : "framework";
    setBusy(true);
    try {
      const res = target
        ? await importVersionCommit(target.frameworkId, preview.payload)
        : await importFrameworkCommit(preview.payload);
      if (res.frameworkId) {
        // Kalite uyarıları bilinmeyen alan DEĞİL: hiçbir içerik atılmadı → ayrı sayılır.
        const counts = splitImportWarnings(res.warnings ?? []);
        const versionNumber = (preview.payload as { version?: { versionNumber?: string } }).version
          ?.versionNumber;
        const info: ImportedInfo = {
          sectionCount: res.summary.sectionCount,
          questionCount: res.summary.questionCount,
          warningsIgnored: counts.ignored,
          qualityWarnings: counts.quality,
          mode,
          ...(mode === "version"
            ? {
                ...(res.versionId ? { versionId: res.versionId } : {}),
                ...(versionNumber ? { versionNumber } : {}),
              }
            : {}),
        };
        setPreview(null);
        onImported(res.frameworkId, info);
      } else {
        // Önizleme açık kalırsa hata kutusu (zIndex 100) arkasında görünmez kalır — önce kapat.
        setPreview(null);
        setError({ title: res.errors[0] ?? "İçe aktarma başarısız", hints: [] });
      }
    } catch (err) {
      setPreview(null);
      setError({
        title: err instanceof ApiError ? err.message : "İçe aktarma başarısız",
        hints: hintsForApiError(err),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen((o) => !o)}
        className="rounded-lg px-3 py-1.5 text-sm"
        style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)" }}
      >
        ⋯ Aktarım
      </button>
      {open && (
        <div
          className="absolute left-0 mt-1 rounded-lg p-1"
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            zIndex: 30,
            minWidth: 220,
          }}
        >
          <button
            type="button"
            disabled={!frameworkId || !versionId}
            onClick={() => void handleExport()}
            className="block w-full rounded px-3 py-2 text-left text-sm"
          >
            ⬆ Dışa Aktar (seçili versiyon)
          </button>
          <button
            type="button"
            onClick={() => {
              modeRef.current = "framework";
              fileRef.current?.click();
            }}
            className="block w-full rounded px-3 py-2 text-left text-sm"
          >
            ⬇ İçe Aktar (yeni çerçeve)
          </button>
          <button
            type="button"
            disabled={!frameworkId}
            title={
              frameworkId
                ? "Dosyayı bu çerçeveye yeni taslak sürüm olarak ekle"
                : "Önce bir çerçeve seçin"
            }
            onClick={() => {
              modeRef.current = "version";
              fileRef.current?.click();
            }}
            className="block w-full rounded px-3 py-2 text-left text-sm"
            style={{ opacity: frameworkId ? 1 : 0.5 }}
          >
            ⬇ Bu çerçeveye yeni sürüm olarak içe aktar
          </button>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(e) => void handleFile(e)}
      />

      {error && <ErrorOverlay box={error} onClose={() => setError(null)} />}

      {preview && (
        <PreviewDialog
          result={preview.result}
          busy={busy}
          onCancel={() => setPreview(null)}
          onConfirm={() => void confirmImport()}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ErrorOverlay({ box, onClose }: { box: ErrorBox; onClose: () => void }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const text = [box.title, ...box.hints].join("\n");
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100 }} onClick={onClose}>
      <div
        role="alert"
        className="rounded-xl p-4"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          top: 80,
          left: "50%",
          transform: "translateX(-50%)",
          width: "min(640px, calc(100vw - 32px))",
          background: "var(--color-surface)",
          border: "1px solid var(--color-error)",
          fontSize: "0.9rem",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: box.hints.length ? 8 : 0 }}>{box.title}</div>
        {box.hints.length > 0 && (
          <ul style={{ paddingLeft: 18, margin: 0, lineHeight: 1.5 }}>
            {box.hints.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          {box.hints.length > 0 && (
            <CopyButton
              text={text}
              label="Hata mesajını kopyala"
              copied={copied}
              onCopied={setCopied}
            />
          )}
          <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm">
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyButton({
  text,
  label,
  copied,
  onCopied,
}: {
  text: string;
  label: string;
  copied: boolean;
  onCopied: (v: boolean) => void;
}): JSX.Element {
  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      onCopied(true);
      window.setTimeout(() => onCopied(false), 2000);
    } catch {
      /* clipboard izni yoksa sessiz — kullanıcı elle seçebilir */
    }
  }
  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => void copy()}
      className="rounded-lg px-3 py-1.5 text-sm"
      style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)" }}
    >
      {copied ? "Kopyalandı" : "⧉ " + label}
      <span
        aria-live="polite"
        style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}
      >
        {copied ? "Kopyalandı" : ""}
      </span>
    </button>
  );
}

// İlk N madde + "+N daha" toggle — sessiz kesme yok (D4).
function ExpandableList({
  items,
  color,
  testId,
}: {
  items: string[];
  color: string;
  testId: string;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, LIST_PREVIEW_COUNT);
  const hidden = items.length - shown.length;
  return (
    <div data-testid={testId} style={{ maxHeight: "40vh", overflowY: "auto" }}>
      <ul
        style={{ color, fontSize: "0.82rem", margin: "4px 0 0", paddingLeft: 18, lineHeight: 1.5 }}
      >
        {shown.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-sm"
          style={{ color: "var(--color-primary)", marginTop: 4, paddingLeft: 18 }}
        >
          +{hidden} daha
        </button>
      )}
    </div>
  );
}

function PreviewDialog({
  result,
  busy,
  onCancel,
  onConfirm,
}: {
  result: ImportResult;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const titleId = useId();
  const helpId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const errors = result.errors;
  const warnings = result.warnings ?? [];
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;
  const { summary } = result;
  const target = summary.target;
  // Şema hatasında dosya hiç ayrıştırılamadığı için fark listeleri (added/removed/typeChanged)
  // her zaman boş gelir — bu durumda "soru listesi değişmiyor"/"0 bölüm · 0 soru" YANLIŞ olur.
  const parsedOk = summary.sectionCount > 0 || summary.questionCount > 0;

  // Açılışta odak başlığa; Esc kapatır; Tab odak tuzağı (D12)
  useEffect(() => {
    titleRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const copyText = [...errors.map((e) => `HATA: ${e}`), ...warnings.map((w) => `UYARI: ${w}`)].join(
    "\n",
  );

  const summaryLine = (
    <p
      style={{
        marginBottom: 8,
        color: hasErrors ? "var(--color-text-3)" : "var(--color-text)",
        fontSize: hasErrors ? "0.85rem" : undefined,
      }}
    >
      <strong style={{ color: hasErrors ? "var(--color-text-3)" : undefined }}>
        {summary.frameworkName}
      </strong>{" "}
      — {summary.sectionCount} bölüm · {summary.questionCount} soru
      {summary.compositeCount > 0 && ` · ${summary.compositeCount} çoklu alanlı soru`}
    </p>
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="rounded-xl p-6 max-w-2xl"
        style={{
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          width: "min(720px, calc(100vw - 32px))",
        }}
      >
        <h2
          id={titleId}
          ref={titleRef}
          tabIndex={-1}
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.1rem",
            marginBottom: 8,
            outline: "none",
          }}
        >
          {hasErrors ? (
            <>
              Dosya henüz içe aktarılamıyor — <strong>{errors.length} hata</strong>
            </>
          ) : (
            "İçe Aktarma Önizlemesi"
          )}
        </h2>
        {hasErrors && (
          <p style={{ fontSize: "0.88rem", marginBottom: 12 }}>
            Aşağıdaki listeyi kopyalayıp ajana yapıştırın, düzeltmesini isteyin (00-BURADAN-BASLAYIN
            3. adım).
          </p>
        )}

        {hasErrors && (
          <section role="alert" aria-label="Hatalar" style={{ marginBottom: hasWarnings ? 12 : 8 }}>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "var(--color-error)" }}>
              Hatalar ({errors.length}) — düzeltilmeden içe aktarılamaz
            </div>
            <ExpandableList items={errors} color="var(--color-error)" testId="import-errors" />
          </section>
        )}

        {hasWarnings && (
          <section
            aria-live="polite"
            aria-label="Uyarılar"
            style={{
              background: "var(--color-warning-bg)",
              border: "1px solid var(--color-warning-border)",
              borderRadius: 8,
              padding: "8px 12px",
              marginBottom: 12,
            }}
          >
            <div
              style={{ fontSize: "0.8rem", fontWeight: 700, color: "var(--color-warning-text)" }}
            >
              Uyarılar ({warnings.length}) — içe aktarmayı engellemez
            </div>
            {/* K7 D3 — bölümle hiç kesişmeyen dönem seçen sorular: soru hiç sorulmayacak. */}
            {countZeroEffectivePeriodWarnings(warnings) > 0 && (
              <div style={{ fontSize: "0.8rem", marginTop: 4, color: "var(--color-warning-text)" }}>
                {countZeroEffectivePeriodWarnings(warnings)} soru hiçbir dönemde sorulmayacak
              </div>
            )}
            <ExpandableList items={warnings} color="#7a3e00" testId="import-warnings" />
          </section>
        )}

        {!hasErrors && summaryLine}

        {/* FW-04 — hedefli import: ne olacağı (ad değişmez, L2/açıklama koşullu) + fark özeti. */}
        {target && (
          <section
            data-testid="import-target"
            style={{
              border: "1px solid var(--color-border)",
              borderRadius: 8,
              padding: "8px 12px",
              marginBottom: 12,
              fontSize: "0.85rem",
              lineHeight: 1.5,
            }}
          >
            <div>
              <strong>Hedef:</strong> {targetLine(summary)}
            </div>
            <div style={{ color: "var(--color-text-3)", fontSize: "0.8rem" }}>
              Ad değişmez: {target.frameworkName} — dosyadaki çerçeve adı bu yolda yok sayılır.
            </div>
            <div style={{ color: "var(--color-text-3)", fontSize: "0.8rem" }}>
              L2 prompt ve açıklama dosyada doluysa güncellenir, boşsa korunur.
            </div>
            {target.removed.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: "0.8rem", fontWeight: 700 }}>
                  Çıkan sorular ({target.removed.length})
                </div>
                <ExpandableList
                  items={target.removed}
                  color="var(--color-text-2)"
                  testId="import-target-removed"
                />
              </div>
            )}
            {target.typeChanged.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: "0.8rem", fontWeight: 700 }}>
                  Tip değişen sorular ({target.typeChanged.length})
                </div>
                <ExpandableList
                  items={target.typeChanged}
                  color="var(--color-text-2)"
                  testId="import-target-type-changed"
                />
              </div>
            )}
            {/* K7 D3 — dönem değişiklikleri: optional alan, backend her zaman doldurur. */}
            {target.periodChanged && target.periodChanged.length > 0 && (
              <details style={{ marginTop: 6 }} data-testid="import-target-period-changed">
                <summary style={{ fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}>
                  Dönem değişikliklerini göster ({target.periodChanged.length})
                </summary>
                <ExpandableList
                  items={target.periodChanged.map(periodChangeLine)}
                  color="var(--color-text-2)"
                  testId="import-target-period-changed-list"
                />
              </details>
            )}
          </section>
        )}

        {/* Kit sektör blokları — bilgi satırı; yayınlarken modalde onaylanır. */}
        {!hasErrors && summary.sectorBlocks.length > 0 && (
          <p
            style={{ color: "var(--color-text-2)", fontSize: "0.85rem", marginBottom: 8 }}
            data-testid="import-sector-blocks"
          >
            Yayınlarken {summary.sectorBlocks.length} sektör paketi türetilebilir:{" "}
            {summary.sectorBlocks.map(stripSectorPrefix).join(", ")}
          </p>
        )}

        {!target && summary.nameCollision && (
          <p style={{ color: "var(--color-warning-text)", fontSize: "0.85rem", marginBottom: 8 }}>
            Aynı isimde çerçeve zaten var (yine de yeni bir kayıt oluşturulur).
          </p>
        )}
        {hasErrors && parsedOk && summaryLine}

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
          <span id={helpId} style={{ fontSize: "0.78rem", color: "var(--color-text-3)", flex: 1 }}>
            {hasErrors
              ? "Hataları düzeltip dosyayı yeniden yükleyin."
              : hasWarnings
                ? "Uyarılar içe aktarmayı engellemez; yine de düzeltip tekrar yükleyebilirsiniz."
                : ""}
          </span>
          {(hasErrors || hasWarnings) && (
            <CopyButton
              text={copyText}
              label="Hata ve uyarıları kopyala"
              copied={copied}
              onCopied={setCopied}
            />
          )}
          <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-sm">
            İptal
          </button>
          <button
            type="button"
            disabled={busy || hasErrors}
            title={hasErrors ? "Önce hataları düzeltin" : undefined}
            aria-describedby={hasErrors ? helpId : undefined}
            onClick={onConfirm}
            className="rounded-lg px-4 py-2 text-sm font-semibold"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              opacity: hasErrors ? 0.5 : 1,
            }}
          >
            İçe Aktar
          </button>
        </div>
      </div>
    </div>
  );
}
